import { Badge, Button, FluentProvider, SearchBox, ToggleButton, webLightTheme } from '@fluentui/react-components';
import { ColumnTripleEditRegular, DataAreaRegular } from '@fluentui/react-icons';
import React from 'react';
import { CustomPanelType } from '../../../../@data';
import { setPanelType } from '../../../../layout/GridPageV2/@data/mutatorActions';
import { CustomCalender } from '../../components/CustomCalender/CustomCalender';
import { ICustomCalenderDateRange, RangeType } from '../../components/CustomCalender/CustomCalender.types';
import { IPubCenterMetadataElement } from '../../DashboardCards/@data/store/schema';
import { ChartGrain } from '../../DashboardCards/ReportCard/ReportCard.types';
import { capitalizeFirstLetter } from '../../OverviewPage/utils/utils';
import { useReportDetailPageStyles } from '../DetailPage.styles';
import { CLARITY_REPORT_NAME, reportTypesWithNoGranularity } from '../utils/utils';
import { FilterComponent } from './FilterComponent';

interface IDataActionProps {
  onSelectedDatesChanged: (selectedDateRange: ICustomCalenderDateRange, rangeType: RangeType, grain?: ChartGrain) => void;
  dateRange: ICustomCalenderDateRange;
  initialDateRangeType: RangeType;
  filtersAvailable: Record<string, IPubCenterMetadataElement[]>;
  selectedFilters: { [key: string]: string[] };
  setSelectedFilters: React.Dispatch<
    React.SetStateAction<{
      [key: string]: string[];
    }>
  >;
  isReportDataLoading: boolean;
  searchQuery: string;
  reportType?: string;
  setSearchQuery: (query: string) => void;
  granularity: string;
  grains?: string[];
  hideChart: boolean;
  setHideChart: (hideChart: boolean) => void;
  isSnapshotReport: boolean;
  isMetadataLoading?: boolean;
}

export const DataActionComponent: React.FC<IDataActionProps> = ({
  onSelectedDatesChanged,
  dateRange,
  initialDateRangeType,
  filtersAvailable,
  selectedFilters,
  setSelectedFilters,
  isReportDataLoading = false,
  searchQuery,
  setSearchQuery,
  granularity,
  grains = [],
  hideChart = false,
  setHideChart,
  reportType,
  isSnapshotReport,
  isMetadataLoading,
}) => {
  const classNames = useReportDetailPageStyles();
  const grainValue = Object.keys(ChartGrain).filter((key) => ChartGrain[key as keyof typeof ChartGrain] === granularity)[0];
  return (
    <FluentProvider theme={webLightTheme}>
      <div className={classNames.dataActionContainerStyle}>
        <div className={classNames.dataActionLeftSideStyle}>
          {!isSnapshotReport && (
            <CustomCalender
              onSelectedDatesChanged={onSelectedDatesChanged}
              initialDateRange={dateRange}
              initialRangeType={initialDateRangeType}
              useUtcDatesOnly={true}
              disabled={isMetadataLoading}
              grains={grains}
              selectedGrain={ChartGrain[grainValue]}
              maxDateDifferenceInDays={
                reportType?.toLowerCase().includes('typetag') || reportType?.toLowerCase().includes('partnertag') ? 365 * 2 : 365 * 5
              }
            />
          )}
          {!isSnapshotReport && !reportTypesWithNoGranularity.has(reportType ?? 'account') && (
            <Badge appearance="outline" color="informative" size="extra-large">
              {capitalizeFirstLetter(grainValue)}
            </Badge>
          )}
          <FilterComponent
            filters={filtersAvailable}
            existingFilters={selectedFilters}
            onSelectedFilterChange={setSelectedFilters}
            reportType={reportType}
            disabled={isMetadataLoading}
          />
        </div>

        <div className={classNames.dataActionRightSideStyle}>
          <SearchBox placeholder="Search" value={searchQuery} onChange={(_, newValue) => setSearchQuery(newValue.value || '')} />

          <Button
            disabled={isReportDataLoading}
            icon={<ColumnTripleEditRegular />}
            appearance="subtle"
            onClick={() => setPanelType(CustomPanelType.ModifyColumns)}
          />

          {!isSnapshotReport && !reportTypesWithNoGranularity.has(reportType ?? 'account') && reportType !== CLARITY_REPORT_NAME && (
            <>
              <div className={classNames.separatorLineStyle} />
              <ToggleButton
                checked={hideChart}
                icon={<DataAreaRegular color="black" />}
                appearance="subtle"
                onClick={() => setHideChart(!hideChart)}
              />
            </>
          )}
        </div>
      </div>
    </FluentProvider>
  );
};
